import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { keys } from "../api/query-registry";
import { hkToday } from "../lib/dates";

export interface WrapUp {
  date: string;
  completedTodos: { id: string; title: string; homeId: string | null; completedAt: string }[];
  carryOver: { id: string; title: string; homeId: string | null; residentId: string | null; dueDate: string }[];
  crossed: { id: string; name: string; bed: string | null; homeId: string; homeName: string; nextIcpDate: string; icpFillStatus: string | null }[];
}

export interface HandoverDraft {
  homeId: string;
  homeName: string;
  code: string | null;
  date: string;
  text: string;
  counts: {
    admissions: { date: string; bed: string; name: string; frequency: string | null; therapist: string | null }[];
    discharges: { date: string; bed: string; name: string; frequency: string | null; therapist: string | null }[];
    firstHalf: { done: number; total: number; axDone: number };
    secondHalf: { done: number; total: number; axDone: number };
  };
}

export function useWrapUp(date?: string) {
  const d = date ?? hkToday();
  return useQuery({ queryKey: keys.wrapUp(d), queryFn: () => api<WrapUp>(`/api/agenda/wrap-up?date=${d}`), staleTime: 30_000 });
}

export function useHandoverDraft(homeId: string | null) {
  return useQuery({
    queryKey: keys.handoverDraft(homeId),
    queryFn: () => api<HandoverDraft>(`/api/handover/generate?homeId=${homeId}`),
    enabled: homeId !== null,
    staleTime: 30_000,
  });
}

/** Saves the OT's edited draft as the home's carry-over source (POST /api/handover/draft). */
export function useSaveHandoverDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ homeId, text }: { homeId: string; text: string }) =>
      api<{ ok: boolean }>("/api/handover/draft", { method: "POST", body: { homeId, text } }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: keys.handoverDraft(vars.homeId) });
    },
  });
}
